import { handleKeyDown } from '@cube3/ui/src/exports/subcomponents/ExportInputPicker';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { add } from '@cube3/ui/src/exports/subcomponents/MultiStringInput/utils/add';
import { remove } from '@cube3/ui/src/exports/subcomponents/MultiStringInput/utils/remove';
import { emailValidators } from '../../../AccountPages/views/LoginView/utils/validators';
import { InputFieldTempate } from '@cube3/cubicle/src/core/atoms/InputField/InputFieldTempate';
import { Container } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import { Panel } from '@cube3/cubicle/src/core/atoms/Panel';
import Loader from '@cube3/cubicle/src/core/atoms/Loader/Loader';
import { uniq } from 'underscore';
import {
  makeSelectedItem,
  makeSuggestionItem
} from './utils/makeSuggestionItem';
import { SuggestionType } from '@cube3/cubicle/src/core/molecules/SuggestionItems/AccountSuggestionItem';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';

export type SuggestionItem = {
  id?: string;
  type?: SuggestionType;
  display_name?: string;
  display_image?: string;
  email_address?: string;
  full_name?: string;
  value?: string;
  disabledReason?: string;
};

export type PrimaryKey = 'id' | 'display_name' | 'value' | 'email_address';

export interface SuggestionsPickerProps {
  type: SuggestionType;
  query: string;
  placeholder?: string;
  setQuery: (input: string) => void;
  /** All suggestions */
  suggestions: SuggestionItem[];
  excludedSuggestions?: SuggestionItem[];
  excludedReason?: string;
  /** The default items that cannot be removed */
  locked?: SuggestionItem[];
  value: SuggestionItem[];
  onChange: Function;
  /**  */
  primaryKey: PrimaryKey;
  loading?: boolean;
  allowAddingOfNewSuggestions?: boolean;
  scrollbarHeight?: number;
  error?: string;
  validator?: (val: string) => string;
  keysListeners?: string[];
}
const emptyArray: SuggestionItem[] = [];

export const SuggestionsPickerBase = (props: SuggestionsPickerProps) => {
  const {
    type,
    primaryKey,
    placeholder,
    query,
    setQuery,
    suggestions: outerSuggestions = emptyArray,
    value: outerValue = emptyArray,
    onChange: onOuterValueChange,
    locked,
    loading,
    allowAddingOfNewSuggestions,
    validator,
    excludedSuggestions,
    excludedReason,
    scrollbarHeight,
    error,
    keysListeners
  } = props;
  /** Internal value controlled by this component, for UI level use */
  const [value, setInnerValue] = useState(outerValue || emptyArray);

  const onChange = useCallback(
    (val) => {
      const deduped = uniq(val, primaryKey || 'id');
      setInnerValue(deduped);
      onOuterValueChange?.(deduped);
    },
    [onOuterValueChange, primaryKey]
  );

  // initialize value
  useEffect(() => {
    if (!value.length && outerValue?.length) {
      setInnerValue(outerValue);
    }
  }, [outerValue]);

  const suggestions = useMemo(() => {
    if (!value?.length) return outerSuggestions;
    return outerSuggestions?.filter(
      (sug) => !value.find((v) => v[primaryKey] === sug[primaryKey])
    );
  }, [outerSuggestions, value]);

  const inputRef = useRef(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement>(null);

  const [focus, handleFocus, handleOff] = useEventBasedToggle();
  const [menuHovered, handleMenuEnter, handleMenuLeave] = useEventBasedToggle();

  const isValid = useMemo(() => {
    if (allowAddingOfNewSuggestions && validator) {
      return !validator(query);
    }
    if (primaryKey === 'email_address' || type === 'email') {
      return !emailValidators(query, undefined, undefined, undefined);
    }
    return allowAddingOfNewSuggestions ? query?.length < 128 : false;
  }, [query, type, allowAddingOfNewSuggestions, primaryKey, validator]);

  const isExcluded = useCallback(
    (item) => {
      return (
        excludedSuggestions?.length &&
        !!excludedSuggestions.find((ex) => ex.id === item.id)
      );
    },
    [excludedSuggestions]
  );

  const [highlightSuggestion, setHighlightSuggestion] = useState<number>(null);
  const handleSuggestionKeys = useCallback(
    (ev: React.KeyboardEvent) => {
      if (suggestions.length) {
        if (ev.key === 'ArrowUp') {
          ev.stopPropagation();
          ev.preventDefault();
          setHighlightSuggestion(
            (c) => (c + suggestions.length - 1) % suggestions.length
          );
        } else if (ev.key === 'ArrowDown') {
          ev.stopPropagation();
          ev.preventDefault();
          setHighlightSuggestion((c) => (c + 1) % suggestions.length);
        } else if (ev.key === 'Enter' && !isValid) {
          // prevent submiting the entire form
          ev.stopPropagation();
          ev.preventDefault();
          if (!ev.shiftKey) {
            setQuery('');
          }
          const focused = suggestions[highlightSuggestion];
          if (focused && !isExcluded(focused)) {
            onChange(
              add(value, {
                ...focused,
                [primaryKey]: focused[primaryKey]
              })
            );
          }
        }
      }
      if (ev.key === 'Backspace' || ev.key === 'Delete') {
        if (!query) {
          onChange(value.slice(0, -1));
        }
      }
    },
    [
      suggestions,
      primaryKey,
      value,
      query,
      setQuery,
      highlightSuggestion,
      isValid
    ]
  );

  const handleCommit = useCallback(
    (evt) => {
      evt.stopPropagation();
      handleKeyDown({
        evt,
        inputValue: query,
        values: value,
        onClear: () => setQuery(''),
        onAdd: (r) => onChange(add(value, { id: r.id || r, [primaryKey]: r })),
        onDelete: (r) =>
          onChange(
            remove(
              value,
              value.find((v) => v[primaryKey] === r)
            )
          ),
        isValid,
        keysListeners
      });
    },
    [isValid, query, value, keysListeners, setQuery, onChange]
  );

  const handleBlur = useCallback(() => {
    if (query && isValid) {
      onChange(add(value, { id: query, [primaryKey]: query }));
    }
    if (!menuHovered) {
      setQuery('');
    }
    handleOff();
  }, [isValid, value, onChange, handleOff, menuHovered, query]);

  useLayoutEffect(() => {
    if (highlightSuggestion !== 0) {
      setHighlightSuggestion(0);
    }
  }, [suggestions]);

  const containerRect = containerRef?.getBoundingClientRect();

  return (
    <div onKeyDownCapture={handleSuggestionKeys} ref={setContainerRef}>
      <InputFieldTempate
        label=""
        useMultiline={true}
        onFocus={() => inputRef.current?.focus()}
        criticalState={query && !isValid}
      >
        <Container
          direction="row"
          style={{
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'start',
            marginTop: 8,
            marginBottom: 8
          }}
          gap={4}
        >
          {locked?.map((r) =>
            makeSelectedItem(r[primaryKey], r.type || type, true)
          )}

          {value
            .filter(
              (r) => !locked?.find((l) => l[primaryKey] === r[primaryKey])
            )
            .map((r) =>
              makeSelectedItem(r[primaryKey], r.type || type, false, () =>
                onChange(remove(value, r))
              )
            )}
          <input
            data-prevent-submit-when-focused={true}
            value={query}
            ref={inputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(ev) => setQuery(ev.target.value)}
            style={{
              display: 'block',
              background: 'transparent',
              color: 'white',
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 50,
              width: 'auto',
              border: 'none',
              minHeight: 32,
              outline: 'none'
            }}
            // Chrome is becoming stricter, and sometimes it ignores autocomplete="off" for login forms.
            // Using autocomplete="new-password" is currently the most reliable.
            autoComplete={'new-password'}
            onKeyDown={handleCommit}
            placeholder={locked?.length ? '' : placeholder}
          />
        </Container>
      </InputFieldTempate>
      {error ? (
        <div style={{ width: containerRect?.width || '100%', margin: '8px 0' }}>
          <Typography color="critical">{error}</Typography>
        </div>
      ) : null}
      {(focus || menuHovered) &&
        (loading ||
          !!suggestions.length ||
          (query && !allowAddingOfNewSuggestions)) && (
          <ContextMenu
            anchor={containerRef}
            width={`${containerRef?.clientWidth}px`}
            scrollbarHeight={
              containerRef
                ? Math.min(
                    scrollbarHeight,
                    window.innerHeight - containerRect.bottom
                  )
                : undefined
            }
          >
            {!loading &&
              query &&
              !suggestions.length &&
              !allowAddingOfNewSuggestions && (
                <Typography colorVariant="03">No results found.</Typography>
              )}
            <div
              onMouseEnter={handleMenuEnter}
              onMouseLeave={handleMenuLeave}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              {loading && <Loader />}
              {suggestions.length > 0 &&
                suggestions.map((item, idx) => {
                  const disabled = isExcluded(item);
                  const config = {
                    active: idx === highlightSuggestion,
                    disabled: disabled,
                    disabledReason: (
                      item['disabledReason'] ||
                      excludedReason ||
                      ''
                    ).toUpperCase(),
                    name: item[primaryKey],
                    onClick: () => {
                      if (disabled) return;
                      inputRef.current.focus();
                      onChange(add(value, s));
                      setQuery('');
                    }
                  };
                  const s = { ...item, ...config, type: item.type || type };
                  return (
                    <Panel
                      backgroundColor={(theme) =>
                        s.active ? theme.color.overlay.primary : 'transparent'
                      }
                      cornerRadius={SizeVariants.lg}
                      key={s.id}
                      style={{
                        padding: 8,
                        cursor: s.disabled ? 'default' : 'pointer'
                      }}
                      hoverBackgroundColor={
                        s.disabled
                          ? undefined
                          : (theme) => theme.color.overlay.hovered
                      }
                      role="option"
                    >
                      {makeSuggestionItem(s, true)}
                    </Panel>
                  );
                })}
            </div>
          </ContextMenu>
        )}
    </div>
  );
};

const useEventBasedToggle = (invert = false) => {
  const [toggled, setToggled] = useState(false);

  const handleOn = useCallback(() => setToggled(!invert), [setToggled]);
  const handleOff = useCallback(() => setToggled(invert), [setToggled]);

  return [toggled, handleOn, handleOff] as [boolean, () => void, () => void];
};

export const WrappedSuggestionPicker = (props) => {
  const { input, ...restProps } = props;
  return <SuggestionsPickerBase {...restProps} {...input} />;
};
